import React from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { MilestoneType } from "../../../../models/milestone";
import { ViewOnly } from "../../../../types";
import { ShipmentObjects } from "../../../Booking/util";
import DeleteConfirmation from "../../../Dialogs/DeleteConfirmation";
import { MilestoneDates } from "../../../Generic/MilestoneDates";
import { useErrorsContext } from "../../hooks/useErrorsContext";
import { useShipmentResourcesContext } from "../../hooks/useResourceContext";
import { useShipmentContext } from "../../hooks/useShipmentContext";
import { BaseFieldProps, ShipmentKey } from "../../types";
import ContainerDetails from "./components/Containers";
import GeneralDetails from "./components/General";
import Section from "./components/Section";
import ShipmentDetails from "./components/ShipmentDetails";
import SkeletonDetails from "./skeleton";
import useStyles from "./styles";

interface DetailsProps {
  cancelShipment: (id: number) => void;
  viewOnly: ViewOnly;
  showDeleteConfirmation?: boolean;
  setShowDeleteConfirmation?: (value: boolean) => void;
}

const Details = ({
  cancelShipment,
  viewOnly: viewMode = { fields: false, actions: false },
  showDeleteConfirmation = false,
  setShowDeleteConfirmation,
}: DetailsProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const { fields: viewOnly } = viewMode;

  const { shipment, handleUpdateShipment, handleChangeOptions, showContainerSection } =
    useShipmentContext();
  const { fieldHasError, getErrorByField } = useErrorsContext();
  const { milestoneDates, loading: loadingResources } = useShipmentResourcesContext();

  const generateBaseFieldProps = (name: ShipmentKey) => {
    const fieldProps: BaseFieldProps<ShipmentKey> = {
      error: fieldHasError(name),
      errorMessage: getErrorByField(name),
      "data-cy": name,
      dataCy: name,
      label: translate(name),
      placeholder: translate(name),
      "aria-label": name,
      disabled: viewOnly,
      name,
    };

    return fieldProps;
  };

  const renderDeleteConfirmationDialog = () => {
    const deleteAction = (id?: number) => {
      if (id) {
        cancelShipment(id);
        if (setShowDeleteConfirmation) setShowDeleteConfirmation(false);
      }
    };

    return (
      <DeleteConfirmation
        showDeleteConfirmation={showDeleteConfirmation}
        setShowDeleteConfirmation={(value: boolean) => {
          if (setShowDeleteConfirmation) setShowDeleteConfirmation(value);
        }}
        deleteAction={() => deleteAction(shipment?.id)}
      />
    );
  };

  const handleUpdateMilestones = (attribute: ShipmentKey, value: any) => {
    handleUpdateShipment(attribute, value);
    handleChangeOptions(ShipmentObjects.MILESTONES);
  };

  return (
    <div data-cy="shipments-detail-box" className={classes.mainDiv}>
      <Grid container direction="column" spacing={2}>
        <Section name={translate("general")}>
          <GeneralDetails generateBaseFieldProps={generateBaseFieldProps} />
        </Section>
        <Section name={translate("shipmentDetails")} className={classes.marginTop}>
          <ShipmentDetails generateBaseFieldProps={generateBaseFieldProps} />
        </Section>
        {showContainerSection && (
          <Section className={classes.marginTop}>
            <ContainerDetails />
          </Section>
        )}
        <MilestoneDates
          type={MilestoneType.ESTIMATED}
          title={translate(`estimatedDates`)}
          values={shipment?.milestones || []}
          options={milestoneDates}
          onUpdate={(key, value) => handleUpdateMilestones(key, value)}
          viewOnly={viewOnly}
          loading={loadingResources}
          fieldSize={3}
        />
        <MilestoneDates
          type={MilestoneType.ACTUAL}
          title={translate(`actualDates`)}
          values={shipment?.milestones || []}
          options={milestoneDates}
          onUpdate={(key, value) => handleUpdateMilestones(key, value)}
          viewOnly={viewOnly}
          loading={loadingResources}
          fieldSize={3}
        />
      </Grid>
      {renderDeleteConfirmationDialog()}
    </div>
  );
};

export { SkeletonDetails };
export default Details;
